const MAX_API_MERCHANT_CODE_LENGTH = 50;
const MAX_MERCHANT_NAME_LENGTH = 23;
const MAX_MERCHANT_IDENTIFICATION_NUMBER_LENGTH = 10;
const MAX_MERCHANT_TOLL_NUMBER_LENGTH = 10;
const MAX_TRUST_ACCOUNT_NUMBER_LENGTH = 20;
const MAX_SFTP_FILE_LOCATION_LENGTH = 255;

const FIELD_ALIASES = {
  apiMerchantCode: ["apiMerchantCode", "api_merchant_code", "APIMerchantCode"],
  merchantName: ["merchantName", "merchant_name", "MerchantName"],
  merchantTollNumber: ["merchantTollNumber", "merchant_toll_number", "TollNumber", "tollNumber"],
  merchantIdentificationNumber: [
    "merchantIdentificationNumber",
    "merchant_identification_number",
    "MerchantIdentificationNumber",
    "merchantIdNumber",
    "merchantId",
  ],
  merchantTrustAccountNumber: [
    "merchantTrustAccountNumber",
    "merchant_trust_account_number",
    "MerchantTrustAccountNumber",
    "trustAccountNumber",
    "trustAccountNo",
  ],
  sftpFileLocation: ["sftpFileLocation", "sftp_file_location", "SFTPFileLocation"],
};

const VALIDATION_MESSAGES = {
  apiMerchantCode: {
    required: "Required.",
    maxLength: "Maximum 50 characters",
    invalid: "Alphanumeric only",
  },
  merchantName: {
    required: "Required.",
    maxLength: "Maximum 23 characters",
  },
  merchantTollNumber: {
    required: "Required.",
    invalid: "Enter a valid 10-digit Toll Number.",
  },
  merchantIdentificationNumber: {
    required: "Required.",
    maxLength: "Maximum 10 characters",
    invalid: "Enter a valid 10-digit numeric value.",
  },
  merchantTrustAccountNumber: {
    required: "Required.",
    invalid: "Maximum 20 digits.",
  },
  sftpFileLocation: {
    required: "Required.",
    maxLength: "Maximum 255 characters",
  },
};

const getTrimmedValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

const getFieldValue = (row, fieldName) => {
  if (!row || typeof row !== "object") {
    return "";
  }

  const aliases = FIELD_ALIASES[fieldName] || [fieldName];
  const matchingKey = aliases.find((alias) => row[alias] !== undefined);

  if (!matchingKey) {
    return "";
  }

  const value = row[matchingKey];
  return value === undefined || value === null ? "" : value;
};

export const validateField = (fieldName, value, row) => {
  const resolvedValue = value !== undefined ? value : getFieldValue(row, fieldName);
  const trimmedValue = getTrimmedValue(resolvedValue);

  switch (fieldName) {
    case "apiMerchantCode": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.apiMerchantCode.required;
      }
      if (trimmedValue.length > MAX_API_MERCHANT_CODE_LENGTH) {
        return VALIDATION_MESSAGES.apiMerchantCode.maxLength;
      }
      return "";
    }
    case "merchantName": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.merchantName.required;
      }
      if (trimmedValue.length > MAX_MERCHANT_NAME_LENGTH) {
        return VALIDATION_MESSAGES.merchantName.maxLength;
      }
      return "";
    }
    case "merchantTollNumber": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.merchantTollNumber.required;
      }
      if (!/^\d{10}$/.test(trimmedValue)) {
        return VALIDATION_MESSAGES.merchantTollNumber.invalid;
      }
      return "";
    }
    case "merchantIdentificationNumber": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.merchantIdentificationNumber.required;
      }
      if (trimmedValue.length > MAX_MERCHANT_IDENTIFICATION_NUMBER_LENGTH) {
        return VALIDATION_MESSAGES.merchantIdentificationNumber.maxLength;
      }
      if (!/^\d{10}$/.test(trimmedValue)) {
        return VALIDATION_MESSAGES.merchantIdentificationNumber.invalid;
      }
      return "";
    }
    case "merchantTrustAccountNumber": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.merchantTrustAccountNumber.required;
      }
      if (!/^\d{1,20}$/.test(trimmedValue)) {
        return VALIDATION_MESSAGES.merchantTrustAccountNumber.invalid;
      }
      return "";
    }
    case "sftpFileLocation": {
      if (!trimmedValue) {
        return VALIDATION_MESSAGES.sftpFileLocation.required;
      }
      if (trimmedValue.length > MAX_SFTP_FILE_LOCATION_LENGTH) {
        return VALIDATION_MESSAGES.sftpFileLocation.maxLength;
      }
      return "";
    }
    default:
      return "";
  }
};

export const validateMerchantRow = (row) => {
  const errors = {};
  const fieldNames = [
    "apiMerchantCode",
    "merchantName",
    "merchantTollNumber",
    "merchantIdentificationNumber",
    "merchantTrustAccountNumber",
    "sftpFileLocation",
  ];

  fieldNames.forEach((fieldName) => {
    const error = validateField(fieldName, undefined, row);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateAllMerchantRows = (rows) => {
  const rowErrors = {};
  const invalidRows = [];

  rows.forEach((row, rowIndex) => {
    const { isValid, errors } = validateMerchantRow(row);
    if (!isValid) {
      rowErrors[rowIndex] = errors;
      invalidRows.push(rowIndex);
    }
  });

  return {
    isValid: invalidRows.length === 0,
    rowErrors,
    invalidRows,
  };
};

export {
  MAX_API_MERCHANT_CODE_LENGTH,
  MAX_MERCHANT_IDENTIFICATION_NUMBER_LENGTH,
  MAX_MERCHANT_NAME_LENGTH,
  MAX_MERCHANT_TOLL_NUMBER_LENGTH,
  MAX_SFTP_FILE_LOCATION_LENGTH,
  MAX_TRUST_ACCOUNT_NUMBER_LENGTH,
  VALIDATION_MESSAGES,
};
